/**
 * Set / clear one account flag (DAT-9005) — `+ Add Lighthouse Account` and the 🗑 on an
 * account header.
 *
 * ONE pair of operations serves every flag; `flag` is a path parameter, so a new flag is a
 * value here and not a new op. Keyed on the (account, flag) pair accordingly: the two flags
 * on one account are independent writes.
 *
 * ⚠️ Clearing a flag does NOT remove the account. The row survives with that flag false —
 * row presence is not a flag — and an unfiltered ListAccountFlags will not return a row
 * whose flags are all false. So the caller must not model `clearFlag` as "delete the
 * account", and must test a flag's VALUE rather than the row's existence.
 *
 * Does NOT refetch and does NOT patch anything: the caller owns the list it renders.
 */

import { useCallback, useRef, useState } from 'react';
import { AccountFlag } from '@amzn/ivthub-client';
import {
  DeleteAccountFlagCommand,
  getIvtClient,
  PutAccountFlagCommand,
} from '../../client/IvtHubClient';
import {
  DENIAL_HINTS,
  type DenialKind,
  denialKindOf,
  hasIvtAdminAccess,
  hasIvtEditorAccess,
  IVT_ADMIN_GROUP,
  IVT_EDITOR_GROUP,
} from '../../utils/authorization';

/**
 * Why a write failed.
 *   - `denied` — 403. A missing role, not a fault; `denial` names which group to ask for.
 *     Never offer a retry.
 *   - `failed` — everything else. The ONLY kind a retry button belongs on.
 *
 * There is deliberately no 404 kind, unlike `useDismissal`: `PutAccountFlag` inserts the row
 * when it is absent, and `DeleteAccountFlag` leaves a nonexistent row alone and calls that
 * success. Neither op declares ResourceNotFoundException, so a 404 here would be a contract
 * change and belongs in `failed` until it is one.
 */
export type AccountFlagFailureKind = 'denied' | 'failed';

export interface AccountFlagFailure {
  kind: AccountFlagFailureKind;
  /** Non-null exactly when `kind === 'denied'`. */
  denial: DenialKind | null;
  /** Ready to render; zh, matching the rest of this page. */
  message: string;
}

export interface AccountFlagState {
  /** True while this (account, flag) pair has a write in flight. */
  isPending: (accountId: string, flag: AccountFlag) => boolean;
  /** The pair's last failure, or null. Dropped when that pair's next write starts. */
  failureOf: (accountId: string, flag: AccountFlag) => AccountFlagFailure | null;
  /** Set the flag true, inserting the account's row if it has none. Null on success. */
  setFlag: (accountId: string, flag: AccountFlag) => Promise<AccountFlagFailure | null>;
  /** Set the flag false. Does NOT remove the row. Null on success. */
  clearFlag: (accountId: string, flag: AccountFlag) => Promise<AccountFlagFailure | null>;
}

/**
 * The LDAP group a flag's write needs. Both routes demand the editor role as a floor;
 * `AccountFlagActivities` additionally requires `ivthub-admin` for BIG_BET, because one
 * route entry can carry only one requirement for every flag.
 */
export function requiredGroupFor(flag: AccountFlag): string {
  return flag === AccountFlag.BIG_BET ? IVT_ADMIN_GROUP : IVT_EDITOR_GROUP;
}

/**
 * Whether to OFFER the affordance at all, so a caller can hide a button that would 403.
 *
 * UX ONLY — the service's Layer-2 gate plus `AccountFlagActivities` are the enforcement, so
 * a caller must still handle a `denied` failure from the write itself.
 */
export function canWriteFlag(
  flag: AccountFlag,
  ldapGroups: readonly string[] | undefined,
): boolean {
  if (flag === AccountFlag.BIG_BET) return hasIvtAdminAccess(ldapGroups);
  return hasIvtEditorAccess(ldapGroups);
}

const keyOf = (accountId: string, flag: AccountFlag) => `${accountId}\u0000${flag}`;

function classify(e: unknown): AccountFlagFailure {
  // Denial FIRST, and before the message is flattened: `denialKindOf` reads `reason`,
  // `name` and `$metadata.httpStatusCode` off the thrown object, none of which survive
  // `(e as Error).message`. A BIG_BET refusal for a non-admin arrives here — whichever of
  // the 403 shapes the service uses, `denialKindOf` already covers all of them.
  const denial = denialKindOf(e);
  if (denial !== null) return { kind: 'denied', denial, message: DENIAL_HINTS[denial] };
  const message = (e as Error | null)?.message;
  return {
    kind: 'failed',
    denial: null,
    message: message ? `操作失败，请重试。（${message}）` : '操作失败，请重试。',
  };
}

export function useAccountFlag(): AccountFlagState {
  const [state, setState] = useState<{
    pending: ReadonlySet<string>;
    failures: ReadonlyMap<string, AccountFlagFailure>;
  }>({ pending: new Set(), failures: new Map() });

  // Ref rather than the rendered `pending` set: a state updater is not guaranteed to run
  // synchronously at dispatch time, so reading the guard out of state can miss a
  // double-fire and send the write twice.
  const inFlight = useRef<Set<string>>(new Set());

  const run = useCallback(
    async (
      accountId: string,
      flag: AccountFlag,
      send: () => Promise<unknown>,
    ): Promise<AccountFlagFailure | null> => {
      const key = keyOf(accountId, flag);
      // Already in flight for this pair — a UI guard, not a failure to report.
      if (inFlight.current.has(key)) return null;
      inFlight.current.add(key);

      setState((s) => {
        const pending = new Set(s.pending);
        pending.add(key);
        const failures = new Map(s.failures);
        failures.delete(key);
        return { pending, failures };
      });

      try {
        await send();
        return null;
      } catch (e) {
        const failure = classify(e);
        setState((s) => {
          const failures = new Map(s.failures);
          failures.set(key, failure);
          return { ...s, failures };
        });
        return failure;
      } finally {
        inFlight.current.delete(key);
        setState((s) => {
          if (!s.pending.has(key)) return s;
          const pending = new Set(s.pending);
          pending.delete(key);
          return { ...s, pending };
        });
      }
    },
    [],
  );

  const setFlag = useCallback(
    (accountId: string, flag: AccountFlag) =>
      run(accountId, flag, () =>
        getIvtClient().send(new PutAccountFlagCommand({ accountId, flag })),
      ),
    [run],
  );

  const clearFlag = useCallback(
    (accountId: string, flag: AccountFlag) =>
      run(accountId, flag, () =>
        getIvtClient().send(new DeleteAccountFlagCommand({ accountId, flag })),
      ),
    [run],
  );

  const isPending = useCallback(
    (accountId: string, flag: AccountFlag) => state.pending.has(keyOf(accountId, flag)),
    [state.pending],
  );

  const failureOf = useCallback(
    (accountId: string, flag: AccountFlag) =>
      state.failures.get(keyOf(accountId, flag)) ?? null,
    [state.failures],
  );

  return { isPending, failureOf, setFlag, clearFlag };
}
